<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import dayjs from '@/lib/dayjs'
import { loadYandexMaps } from '@/lib/ymaps'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { price as formatPrice } from '@/helpers/numberFormatters'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import EventsMap from '@/ui-kit/events/Map.vue'
import EventsReviewsAndQuestions from '@/ui-kit/events/ReviewsAndQuestions.vue'
import EventsReviewModal from '@/ui-kit/events/AddReviewModal.vue'

interface UsefulLink {
  name: string
  url: string
}

interface EventData {
  id: number
  name: string
  type: string
  start_date: string | null
  end_date: string | null
  address: string | null
  current_price: number | null
  short_desc: string
  my_permissions: string
  useful_links: UsefulLink[]
  can_purchase: boolean
  sold_out: boolean
  location: {
    lat: number
    long: number
  }
  my_review?: {
    rating: number
    text: string
    files: Array<{ id: number; filename: string }>
  }
}

interface PageContext {
  event: EventData
  description_html: string
}

interface ReviewModalExpose {
  $open: (event: EventData) => void
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()

const reviewModal = ref<ReviewModalExpose | null>(null)
const mapApiReady = ref(false)

const hasTicketPermission = computed(() => {
  return ctx.event.my_permissions.split(',').includes('ti')
})

const breadcrumbs = computed(() => {
  return [
    { href: '/events', label: 'Мероприятия' },
    { href: '', label: ctx.event.name }
  ]
})

const eventTypeBadge = computed(() => {
  switch (ctx.event.type) {
    case 'ca':
      return { color: 'orange', label: 'Лагерь' }
    case 'co':
      return { color: 'blue', label: 'Конференция' }
    case 'ki':
      return { color: 'green', label: 'Детский лагерь' }
    default:
      return { color: 'blue', label: 'Мероприятие' }
  }
})

const formattedDateSpan = computed(() => {
  if (!ctx.event.start_date || !ctx.event.end_date) return ''

  const start = dayjs(ctx.event.start_date)
  const end = dayjs(ctx.event.end_date)

  const endStr = end.format('D MMMM YYYY')
  if (start.isSame(end)) return endStr

  let startStr = ''
  if (start.isSame(end, 'month')) startStr = start.format('D')
  else if (start.isSame(end, 'year')) startStr = start.format('D MMMM')
  else startStr = start.format('D MMMM YYYY')

  return `с ${startStr} по ${endStr} г.`
})

const eventPassed = computed(() => {
  if (!ctx.event.end_date) return false
  return dayjs(ctx.event.end_date).isBefore(dayjs().subtract(1, 'days'))
})

const isLocationEmpty = computed(() => {
  const { lat, long } = ctx.event.location
  return !lat && !long
})

function formatDate(value: string): string {
  return dayjs(value).format('D MMMM YYYY')
}

function formatRub(value: number | null): string {
  return formatPrice.rub(value || 0)
}

function reload() {
  window.location.reload()
}

function openReviewModal() {
  reviewModal.value?.$open(ctx.event)
}

onMounted(async () => {
  if (hasTicketPermission.value || eventPassed.value) {
    try {
      await loadYandexMaps()
      mapApiReady.value = true
    } catch (error) {
      console.error(error)
    }
  }
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content events-description">
      <v-notifications position="bottom right" />

      <v-breadcrumbs v-if="!isMobile" :items="breadcrumbs" />
      <h1 class="header">О мероприятии</h1>
      <span class="events-description__badge mt-4" :class="eventTypeBadge.color" v-text="eventTypeBadge.label" />

      <h2 class="text-title-bold mt-3" v-text="ctx.event.name" />

      <div class="flex-adaptive mt-3">
        <ul class="events-description__info flex-spacer">
          <li class="d-flex align-center">
            <v-icon class="gray-text mr-1" size="18">mdi-calendar-blank</v-icon>
            <b v-if="ctx.event.start_date">{{ formatDate(ctx.event.start_date) }}</b>
            <b v-else>Ожидайте скоро</b>
          </li>
          <li v-if="ctx.event.address" class="d-flex align-center">
            <v-icon class="gray-text mr-1" size="18">mdi-map-marker</v-icon>
            {{ ctx.event.address }}
          </li>
        </ul>

        <p v-if="ctx.event.current_price" class="text-title-bold">
          {{ formatRub(ctx.event.current_price) }}
        </p>
      </div>

      <p class="mt-3" v-text="ctx.event.short_desc" />

      <p v-if="ctx.event.start_date && ctx.event.end_date" class="mt-3 text-body-bold">
        Время проведения: <span class="red-text">{{ formattedDateSpan }}</span>
      </p>

      <ul v-if="hasTicketPermission" class="d-flex flex-column mt-1">
        <li v-for="link in ctx.event.useful_links" :key="link.url">
          <v-link class="mt-1 text-body-bold" :href="link.url" target="_blank" mode="primary">
            {{ link.name }}
          </v-link>
        </li>
      </ul>
      <div v-else-if="ctx.event.can_purchase" class="d-flex mt-3">
        <v-button mode="primary large" :href="`/events/purchase_event/${ctx.event.id}`" :disabled="ctx.event.sold_out">
          {{ ctx.event.sold_out ? 'Места закончились' : 'Записаться' }}
        </v-button>
      </div>

      <hr class="events-description__divider mt-5" />

      <div class="mt-3" v-html="ctx.description_html" />

      <template v-if="hasTicketPermission || eventPassed">
        <hr class="events-description__divider mt-5" />

        <h2 class="text-title-bold mt-3">Место проведения</h2>
        <p class="d-flex align-center mt-3">
          <v-icon class="gray-text mr-1" size="18">mdi-map-marker</v-icon>
          {{ ctx.event.address }}
        </p>

        <EventsMap v-if="mapApiReady && !isLocationEmpty" class="mt-2" :location="ctx.event.location" />

        <ul v-if="hasTicketPermission" class="d-flex flex-column mt-2">
          <li v-for="link in ctx.event.useful_links" :key="link.url">
            <v-link class="mt-1 text-body-bold" :href="link.url" target="_blank" mode="primary">
              {{ link.name }}
            </v-link>
          </li>
        </ul>
        <div v-else-if="ctx.event.can_purchase" class="d-flex mt-3">
          <v-button
            mode="primary large"
            :href="`/events/purchase_event/${ctx.event.id}`"
            :disabled="ctx.event.sold_out"
          >
            {{ ctx.event.sold_out ? 'Места закончились' : 'Записаться' }}
          </v-button>
        </div>
      </template>

      <hr class="events-description__divider mt-5" />

      <h2 class="text-title-bold mt-3">Отзывы и вопросы по мероприятию</h2>
      <v-button v-if="eventPassed && hasTicketPermission" mode="primary" class="mt-3" @click="openReviewModal">
        {{ ctx.event.my_review ? 'Редактировать мой отзыв' : 'Оставить отзыв' }}
      </v-button>

      <EventsReviewsAndQuestions
        id="reviews"
        class="mt-3"
        :event-id="ctx.event.id"
        :disable-tabs="!eventPassed"
        :tab="!eventPassed && !hasTicketPermission ? 1 : 0"
      />

      <EventsReviewModal ref="reviewModal" :standalone="false" @close="reload" />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/events/description.scss' as *;
</style>
